import { Button, LinearProgress, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { Alert } from '@material-ui/lab';
import React, { ReactNode, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import axios from '../../../axiosCF';
import Attachments from '../../../components/Attachments/Attachments';
import CustomDialog from '../../../components/UI/CustomDialog/CustomDialog';
import Input, { EInputType } from '../../../components/UI/Input/Input';
import withErrorHandler from '../../../hoc/withErrorHandler/withErrorHandler';
import { ESuekEducationStatus, IAttachment, ISuekEducation, ITargetGroup, EParticipantDataCollection, SUEK_TOPIC_OPTIONS, ESUEKEducationDropzoneType } from '../../../interfaces/domain';
import { IAppState } from '../../../interfaces/state';
import { defaultInputChangedHandler, getFormData, IControls, IInputConfigOptional, initForm } from '../../../shared/utility';
import * as actions from '../../../store/actions';

import classes from "./SUEKEducation.module.scss";

interface IDisplayData {
  text: string;
  value: ReactNode;
}

enum EFields {
  participantsReality = "participantsReality",
  participantDataCollection = "participantDataCollection",
  subscriberFeedback = "subscriberFeedback",
  participantsFeedback = "participantsFeedback",
  topics = "topics",
  implementationAndMaterials = "implementationAndMaterials",
  lecturerComments = "lecturerComments",
}

const initControls: IControls = {
  controls: {
    [EFields.participantsReality]: {
      type: EInputType.number,
      value: "",
      label: "Osallistujia (toteutunut)",
      valid: true,
    },
    [EFields.participantDataCollection]: {
      type: EInputType.select,
      value: "",
      label: "Osallistujatiedot kerätty",
      options: [
        { value: EParticipantDataCollection.QR, text: "qr-koodilla" },
        {
          value: EParticipantDataCollection.SUBSCRIBER_DELIVERS,
          text: "sovittu, että tilaaja toimittaa osallistujatiedot SUEKille",
        },
        { value: EParticipantDataCollection.PAPER, text: "paperilla (liitä kuva)" },
      ],
      valid: true,
    },
    [EFields.subscriberFeedback]: {
      type: EInputType.text,
      value: "",
      label: "Tilaajapalaute",
      multiline: true,
      valid: true,
    },
    [EFields.participantsFeedback]: {
      type: EInputType.text,
      value: "",
      label: "Osallistujan palaute",
      multiline: true,
      valid: true,
    },
    [EFields.topics]: {
      type: EInputType.select,
      value: [],
      label: "Valitse kaikki koulutuksessa läpikäydyt aihealueet",
      multiple: true,
      options: SUEK_TOPIC_OPTIONS,
      valid: true,
    },
    [EFields.implementationAndMaterials]: {
      type: EInputType.text,
      value: "",
      label: "Kuvaa tarkasti koulutuksen toteutustapa ja käytettyjä materiaaleja",
      multiline: true,
      valid: true,
    },
    [EFields.lecturerComments]: {
      type: EInputType.text,
      value: "",
      label: "Luennoitsijan kommentit (esim. onnistumiset/haasteet, palautetta)",
      multiline: true,
      valid: true,
    },
  },
  isValid: false,
};

interface IStateProps {
  suekEducation: ISuekEducation | null;
  loading: boolean;
  uploading: boolean;
}

interface IDispatchProps {
  onGetSuekEducation: (id: number) => void;
  onGetSuekEducationClear: () => void;
  onUpdateSuekEducation: (education: ISuekEducation) => Promise<ISuekEducation>;
  onUploadSuekEducationFiles: (formData: FormData) => Promise<void>;
  onDeleteSuekEducationFile: (
    id: number,
    name: string,
    type: ESUEKEducationDropzoneType
  ) => Promise<void>;
}

interface IMatch {
  id: string;
}

interface IProps
  extends IDispatchProps,
    IStateProps,
    RouteComponentProps<IMatch> {}

const SUEKEducation: React.FC<IProps> = ({
  onGetSuekEducation,
  onUpdateSuekEducation,
  suekEducation,
  loading,
  uploading,
  history,
  match,
  onUploadSuekEducationFiles,
  onDeleteSuekEducationFile,
  onGetSuekEducationClear,
}) => {
  const [targetGroupAmounts, setTargetGroupAmounts] = useState<ITargetGroup[]>(
    []
  );
  const [controls, setControls] = useState(initControls);
  const [isOpen, setOpen] = useState(false);
  const [doInit, setDoInit] = useState(true);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [activeUploadType, setActiveUploadType] =
    useState<ESUEKEducationDropzoneType | null>(null);

  const { id } = match.params;

  useEffect(() => {
    onGetSuekEducation(parseInt(id));
    return () => {
      onGetSuekEducationClear();
    };
  }, [onGetSuekEducation, id, onGetSuekEducationClear]);

  useEffect(() => {
    if (suekEducation && doInit) {
      const { status } = suekEducation;
      setControls(
        initForm(
          initControls.controls,
          suekEducation,
          status === ESuekEducationStatus.DONE
        )
      );
      setTargetGroupAmounts(suekEducation.targetGroups || []);
      setDoInit(false);
    }
    // eslint-disable-next-line
  }, [suekEducation]);

  const trainingMaterialAttachments =
    suekEducation && suekEducation.attachments
      ? suekEducation.attachments.filter(
          (a) => a.type === ESUEKEducationDropzoneType.TRAINING_MATERIAL
        )
      : [];
  const hasTrainingMaterial = trainingMaterialAttachments.length > 0;
  const hasImplementationText =
    String(
      controls.controls[EFields.implementationAndMaterials].value || ""
    ).trim().length > 0;
  const isTrainingMaterialOrImplementationValid =
    hasTrainingMaterial || hasImplementationText;

  const onSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    if (!suekEducation || uploading) return;
    if (!isTrainingMaterialOrImplementationValid) {
      setSubmitAttempted(true);
      return;
    }

    const newEducation = getFormData(controls.controls) as ISuekEducation;
    newEducation.id = suekEducation.id;
    newEducation.targetGroups = targetGroupAmounts;

    const updatedSuekEducation = await onUpdateSuekEducation(newEducation);

    if (updatedSuekEducation && updatedSuekEducation.id) {
      setOpen(true);
    }
  };

  const onResetForm = () => {
    setSubmitAttempted(false);
    if (suekEducation) {
      setControls(
        initForm(
          initControls.controls,
          suekEducation,
          suekEducation.status === ESuekEducationStatus.DONE
        )
      );
      setTargetGroupAmounts(suekEducation.targetGroups || []);
    } else {
      setControls(initControls);
    }
  };

  const onCloseDialog = () => {
    history.push("/educations");
  };

  if (loading) {
    return <LinearProgress color="primary" />;
  }

  if (!suekEducation) return null;

  const createDisplayData = (displayData: IDisplayData[]) => {
    return (
      <Table size="small">
        <TableBody>
          {displayData.map((item) => (
            <TableRow key={item.text}>
              <TableCell>{item.text}:</TableCell>
              <TableCell>{item.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const onDropHandler = async (files: File[], type: ESUEKEducationDropzoneType) => {
    if (!files || files.length === 0 || uploading) return;

    const existingNames = new Set(
      (suekEducation.attachments || [])
        .filter((a) => a.type === type)
        .map((a) => a.name.toLowerCase())
    );

    const uniqueFiles = files.filter(
      (file, index, self) =>
        !existingNames.has(file.name.toLowerCase()) &&
        self.findIndex(
          (f) => f.name.toLowerCase() === file.name.toLowerCase()
        ) === index
    );

    if (uniqueFiles.length === 0) return;

    const formData = new FormData();
    uniqueFiles.forEach((file, index) => {
      formData.append(`file_${index + 1}`, file);
    });
    formData.append("fileAmount", uniqueFiles.length.toString());
    formData.append("id", suekEducation.id.toString());
    formData.append("type", type);

    setActiveUploadType(type);
    await onUploadSuekEducationFiles(formData);
    setActiveUploadType(null);
  };

  const onDeleteAttachmentHandler = async (attachment: IAttachment) => {
    if (!attachment.type || uploading) return;
    const type = attachment.type as ESUEKEducationDropzoneType;
    setActiveUploadType(type);
    await onDeleteSuekEducationFile(
      suekEducation.id,
      attachment.name,
      type
    );
    setActiveUploadType(null);
  };

  const {
    attachments,
    name,
    place,
    additionalInfo,
    theme,
    startDate,
    endDate,
    duration,
    sport,
    targetGroups,
    contactPerson,
    participantsEstimate,
    subscriber,
    status,
  } = suekEducation;

  const displayData = [
    { text: "Koulutuksen nimi", value: name },
    { text: "Paikka", value: place },
    { text: "Lisätiedot", value: additionalInfo },
    { text: "Teema", value: theme ? theme.join(", ") : "" },
    {
      text: "Ajankohta",
      value: `${startDate} ${endDate ? `- ${endDate}` : ""}`,
    },
    { text: "Kesto", value: duration },
    { text: "Laji", value: sport },
    {
      text: "Kohderyhmä",
      value: targetGroups.map((targetGroup) => targetGroup.name).join(", "),
    },
    { text: "Yhteyshenkilö", value: contactPerson },
    { text: "Osallistujia (arvioitu)", value: participantsEstimate },
    { text: "Tilaajataho", value: subscriber },
    { 
      text: "Liitteet", 
      value: <Attachments attachments={attachments ? attachments.filter(a => !a.type) : []} /> 
    },
  ];

  const createInput = (inputName: EFields, config?: IInputConfigOptional) => {
    return (
      <Input
        formControl={{
          id: inputName,
          config: {
            ...controls.controls[inputName],
            ...config,
          },
        }}
        changed={(value, controlName: string) =>
          defaultInputChangedHandler(value, controlName, setControls)
        }
      />
    );
  };

  return (
    <React.Fragment>
      <CustomDialog
        title="Kiitos"
        content="Koulutuksen lähettäminen onnistui."
        buttonText="Sulje"
        onOk={onCloseDialog}
        open={isOpen}
      />

      <Typography variant="h5" gutterBottom>
        Koulutuksen tiedot
      </Typography>

      <Paper elevation={4}>{createDisplayData(displayData)}</Paper>
      <br />
      <Typography variant="h5" gutterBottom>
        Täytettävät tiedot
      </Typography>
      <Paper elevation={4} className={classes.Paper}>
        <form onSubmit={onSubmit} noValidate>
          {createInput(EFields.participantsReality)}
          {createInput(EFields.participantDataCollection)}
          {controls.controls[EFields.participantDataCollection].value ===
            EParticipantDataCollection.PAPER && (
            <React.Fragment>
              {status === ESuekEducationStatus.OPEN && (
                <>
                <Typography variant="body2" gutterBottom>
                  Lataa kuva paperisesta osallistujalistasta
                </Typography>
                <Dropzone
                  disabled={uploading}
                  onDrop={(files) => onDropHandler(files, ESUEKEducationDropzoneType.PARTICIPANT_LIST)}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => {
                    const classNames = [classes.Dropzone];
                    if (isDragActive) {
                      classNames.push(classes.DropzoneDragActive);
                    }
                    return (
                      <div {...getRootProps()} className={classNames.join(" ")}>
                        <input {...getInputProps()} />
                        <p>
                          {uploading && activeUploadType === ESUEKEducationDropzoneType.PARTICIPANT_LIST
                            ? "Ladataan liitettä..."
                            : isDragActive
                            ? "Tiedosto(t) ladataan kun päästät irti."
                            : "Lataa liitteet painamalla tästä tai pudottamalla tiedostot."}
                        </p>
                      </div>
                    );
                  }}
                </Dropzone>
                </>
              )}
              <Attachments
                attachments={attachments ? attachments.filter(a => a.type === ESUEKEducationDropzoneType.PARTICIPANT_LIST) : []}
                onDelete={status === ESuekEducationStatus.OPEN ? onDeleteAttachmentHandler : undefined}
                uploading={uploading && activeUploadType === ESUEKEducationDropzoneType.PARTICIPANT_LIST}
                showTitle
              />
            </React.Fragment>
          )}
          {targetGroupAmounts.map((targetGroup) => (
            <TargetGroup
              key={targetGroup.id}
              targetGroup={targetGroup}
              onUpdate={(id: string, amount: number) => {
                setTargetGroupAmounts(prev => prev.map(prev => prev.id === id ? {...prev, amount } : prev));
              }}
              disabled={suekEducation.status === ESuekEducationStatus.DONE}
            />
          ))}

          {createInput(EFields.subscriberFeedback)}
          {createInput(EFields.participantsFeedback)}
          {createInput(EFields.topics)}
          {status === ESuekEducationStatus.OPEN ? (
          <React.Fragment>
            <p>
              Lataa aina käyttämäsi koulutusmateriaali tai mikäli et käyttänyt diaesitystä, kuvaa alle tarkasti koulutuksen toteutustapa
            </p>
            <Dropzone
              disabled={uploading}
              onDrop={(files) => onDropHandler(files, ESUEKEducationDropzoneType.TRAINING_MATERIAL)}
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                const classNames = [classes.Dropzone];
                if (isDragActive) {
                  classNames.push(classes.DropzoneDragActive);
                }
                return (
                  <div {...getRootProps()} className={classNames.join(" ")}>
                    <input {...getInputProps()} />
                    <p>
                      {uploading && activeUploadType === ESUEKEducationDropzoneType.TRAINING_MATERIAL
                        ? "Ladataan liitettä..."
                        : isDragActive
                        ? "Tiedosto(t) ladataan kun päästät irti."
                        : "Lataa liitteet painamalla tästä tai pudottamalla tiedostot."}
                    </p>
                  </div>
                );
              }}
            </Dropzone>
          </React.Fragment>
          ) : trainingMaterialAttachments.length > 0 ? <p style={{ marginBottom: '.25rem', color: 'rgba(0, 0, 0, 0.38)' }}>Koulutusmateriaali</p> : null}
          <Attachments
            attachments={trainingMaterialAttachments}
            onDelete={status === ESuekEducationStatus.OPEN ? onDeleteAttachmentHandler : undefined}
            uploading={uploading && activeUploadType === ESUEKEducationDropzoneType.TRAINING_MATERIAL}
            showTitle={status === ESuekEducationStatus.OPEN}
          />
          {createInput(EFields.implementationAndMaterials, {
            valid: !submitAttempted || isTrainingMaterialOrImplementationValid,
            touched: submitAttempted && !isTrainingMaterialOrImplementationValid,
            validation: {
              required: true,
              or: ["attachments"],
            },
          })}
          {createInput(EFields.lecturerComments)}

          {submitAttempted && !isTrainingMaterialOrImplementationValid && (
            <Alert severity="error" className={classes.Alert}>
              Täytä tai tarkista vielä seuraavat pakolliset kentät:{" "}
              <strong>
                Koulutusmateriaali (liite) tai Kuvaa tarkasti koulutuksen toteutustapa ja käytettyjä materiaaleja
              </strong>
            </Alert>
          )}

          {status === ESuekEducationStatus.OPEN ? (
            <React.Fragment>
              <Button
                color="primary"
                className={classes.Button}
                type="submit"
                disabled={uploading}
              >
                Lähetä
              </Button>
              <Button
                className={classes.Button}
                onClick={onResetForm}
                disabled={uploading}
              >
                Tyhjennä
              </Button>
            </React.Fragment>
          ) : (
            <Button
              color="primary"
              className={classes.Button}
              onClick={() => history.push("/educations")}
            >
              Takaisin
            </Button>
          )}
        </form>
      </Paper>
    </React.Fragment>
  );
};

interface ITargetGroupProps {
  targetGroup: ITargetGroup;
  onUpdate: (id: string, amount: number) => void;
  disabled: boolean;
}

const TargetGroup: React.FC<ITargetGroupProps> = ({
  targetGroup,
  onUpdate,
  disabled
}) => {
  return (
    <Input
      formControl={{
        id: "targetGroup_" + targetGroup.id,
        config: {
          type: EInputType.number,
          value: targetGroup.amount || "",
          label: `Osallistujia (${targetGroup.name})`,
          valid: true,
          disabled
        },
      }}
      changed={(value, controlName: string) =>
        onUpdate(targetGroup.id, value as number)
      }
    />
  );
};

const mapStateToProps = (state: IAppState): IStateProps => {
  return {
    loading: state.suekEducations.loading,
    uploading: state.suekEducations.uploading,
    suekEducation: state.suekEducations.suekEducation,
  };
};

const mapDispatchToProps = (dispatch: any): IDispatchProps => {
  return {
    onGetSuekEducation: (id) => dispatch(actions.getSuekEducation(id)),
    onUpdateSuekEducation: (suekEducation: ISuekEducation) =>
      dispatch(actions.updateSuekEducation(suekEducation)),
    onUploadSuekEducationFiles: (formData) =>
      dispatch(actions.uploadSuekEducationFiles(formData)),
    onDeleteSuekEducationFile: (id, name, type) =>
      dispatch(actions.deleteSuekEducationFile(id, name, type)),
    onGetSuekEducationClear: () => dispatch(actions.getSuekEducationClear()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorHandler(SUEKEducation, axios));
